"use client";

import * as React from "react";
import GridLayout, { Layout, LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import type {
  Chart,
  Report,
  GridPosition,
  ReportQueryResult,
} from "@bi-studio/shared/lib";
import {
  Table as MantineTable,
  useMantineColorScheme,
  Text,
  Pagination,
  ActionIcon,
  Tooltip,
  Box,
  rem,
} from "@mantine/core";
import { SmartCell, evaluateFormatting } from "@bi-studio/shared";
import { BarChart3, Table as TableIcon, Share2 } from "lucide-react";
import { renderChart } from "@/charts";
import html2canvas from "html2canvas";

interface ItemWithLayout {
  id: string;
  chart: Chart | null;
  report: Report | null;
  layout: GridPosition;
  data: ReportQueryResult;
}

interface DashboardViewLayoutProps {
  items: Array<{
    id: string;
    chart: Chart | null;
    report: Report | null;
    data: ReportQueryResult;
    layout?: GridPosition;
  }>;
  token: string;
  onItemPageChange?: (itemId: string, page: number) => void;
}

type ChartDefinition =
  | {
    type: "bar";
    config: any & {
      xField: string;
      yField: string;
      orientation?: "vertical" | "horizontal";
    };
  }
  | {
    type: "line";
    config: any & {
      xField: string;
      yField: string;
      curve?: "linear" | "basis" | "step";
      seriesField?: string;
    };
  }
  | {
    type: "pie";
    config: any & {
      labelField: string;
      valueField: string;
      innerRadius?: number;
    };
  }
  | {
    type: "scorecard";
    config: any & {
      valueField: string;
      rowIndex?: number;
    };
  };

export function DashboardViewLayout({
  items,
  onItemPageChange,
}: DashboardViewLayoutProps) {
  const { colorScheme } = useMantineColorScheme();
  const chartContainerRefs = React.useRef<Map<string, HTMLDivElement>>(
    new Map()
  );
  const itemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = React.useState(1200);
  const [isMobile, setIsMobile] = React.useState(false);

  // Calculate grid width and check mobile status
  React.useEffect(() => {
    const updateLayout = () => {
      if (gridContainerRef.current) {
        const containerWidth = gridContainerRef.current.clientWidth;
        // On mobile, use full width. On desktop, maintain min 800px constraint if desired,
        // but typically we just want it to fit the container.
        // The original code had Math.max(containerWidth - 32, 800)
        // We'll relax this for mobile.
        const isNowMobile = containerWidth < 768; // Standard tablet/mobile breakpoint
        setIsMobile(isNowMobile);

        if (isNowMobile) {
          setGridWidth(Math.max(containerWidth - 32, 300)); // Allow smaller on mobile
        } else {
          setGridWidth(Math.max(containerWidth - 32, 800)); // Keep original constraint for desktop
        }
      }
    };

    updateLayout();
    // Using ResizeObserver is better than window resize for container awareness,
    // but window resize is what was there. Let's stick to window resize for now to match current pattern,
    // but add it to the window.
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  // Convert items to grid layout format
  const gridItems: ItemWithLayout[] = React.useMemo(() => {
    return items.map((item) => {
      const layout = item.layout || { x: 0, y: 0, w: 6, h: 4 };
      return {
        id: item.id,
        chart: item.chart,
        report: item.report,
        layout,
        data: item.data,
      };
    });
  }, [items]);

  // Sorted items for mobile stack view (sort by y first, then x)
  const sortedItems = React.useMemo(() => {
    if (!isMobile) return gridItems;
    return [...gridItems].sort((a, b) => {
      if (a.layout.y !== b.layout.y) return a.layout.y - b.layout.y;
      return a.layout.x - b.layout.x;
    });
  }, [gridItems, isMobile]);

  // Convert to react-grid-layout format (only used for desktop)
  const gridLayout: Layout = React.useMemo(() => {
    return gridItems.map(
      (item): LayoutItem => ({
        i: item.id,
        x: item.layout.x,
        y: item.layout.y,
        w: item.layout.w,
        h: item.layout.h,
        minW: 2,
        minH: 2,
        static: true, // Make items static (non-draggable) for view mode
      })
    );
  }, [gridItems]);

  // Render charts when responsiveness updates
  React.useEffect(() => {
    const renderCharts = () => {
      const itemsToRender = isMobile ? sortedItems : gridItems;

      itemsToRender.forEach((item) => {
        // Only render charts
        if (!item.chart) {
          return;
        }

        const container = chartContainerRefs.current.get(item.id);

        if (
          !container ||
          !item.data ||
          !item.data.rows ||
          item.data.rows.length === 0
        ) {
          return;
        }

        try {
          const definition: ChartDefinition = {
            type: item.chart.type,
            config: item.chart.config,
          } as ChartDefinition;

          // Get container dimensions for responsive sizing
          const containerWidth = container.clientWidth || 300;
          // On mobile, maybe default to a fixed height or square-ish?
          // Desktop preserved current logic, mobile we can make consistent.
          const containerHeight = isMobile ? 300 : container.clientHeight || 300;

          const chartWidth = containerWidth;
          const chartHeight = containerHeight;

          // Clear previous chart if needed (renderChart usually handles this but good to be safe if implementation changes)
          // wrapper.innerHTML = ""; // renderChart seems to append/update. Assuming it works.

          void renderChart(container, item.data.rows, definition, {
            width: chartWidth,
            height: chartHeight,
            margin: { top: 32, right: 24, bottom: 48, left: 56 },
          }, (colorScheme === 'dark' ? 'dark' : 'light'));
        } catch (err) {
          console.error(`Failed to render chart ${item.id}:`, err);
        }
      });
    };

    // Debounce render
    const timeoutId = setTimeout(renderCharts, 100);
    return () => clearTimeout(timeoutId);
  }, [gridItems, sortedItems, gridLayout, isMobile, colorScheme]);

  const setChartRef = (itemId: string) => (el: HTMLDivElement | null) => {
    if (el) {
      chartContainerRefs.current.set(itemId, el);
    } else {
      chartContainerRefs.current.delete(itemId);
    }
  };

  const setItemRef = (itemId: string) => (el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(itemId, el);
    } else {
      itemRefs.current.delete(itemId);
    }
  };

  const handleShare = async (item: ItemWithLayout) => {
    const element = itemRefs.current.get(item.id);
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        useCORS: true,
        backgroundColor: colorScheme === "dark" ? "#1A1B1E" : "#FFFFFF",
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector(`[data-chart-id="${item.id}"]`);
          if (clonedElement) {
            // Expand title
            const titleEl = clonedElement.querySelector('[data-chart-title]');
            if (titleEl) {
              (titleEl as HTMLElement).style.whiteSpace = 'normal';
              (titleEl as HTMLElement).style.overflow = 'visible';
              (titleEl as HTMLElement).style.height = 'auto';
              titleEl.classList.remove('truncate');
            }

            // Hide actions
            const actionEl = clonedElement.querySelector('[data-chart-action]');
            if (actionEl) {
              (actionEl as HTMLElement).style.display = 'none';
            }

            // Hide icon
            const iconEl = clonedElement.querySelector('[data-chart-icon]');
            if (iconEl) {
              (iconEl as HTMLElement).style.display = 'none';
            }

            // Ensure container expands
            (clonedElement as HTMLElement).style.height = 'auto';
            (clonedElement as HTMLElement).style.overflow = 'visible';
          }
        }
      });

      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const fileName = `${item.chart?.config.title || item.report?.name || "chart"}.png`;
        const file = new File([blob], fileName, { type: "image/png" });

        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: item.chart?.config.title || item.report?.name || "Dashboard Item",
            });
          } catch (error) {
            // User cancelled or other error, fallback to download
            if ((error as Error).name !== "AbortError") {
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = fileName;
              link.click();
              URL.revokeObjectURL(url);
            }
          }
        } else {
          // Fallback to download
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
        }
      });
    } catch (err) {
      console.error("Failed to share item:", err);
    }
  };

  const renderItemContent = (item: ItemWithLayout) => {
    const hasData = item.data && item.data.rows && item.data.rows.length > 0;
    const isChart = item.chart !== null;
    const isReport = item.report !== null;

    return (
      <Box
        key={item.id}
        data-chart-id={item.id}
        ref={setItemRef(item.id)}
        style={{
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.7)',
          borderRadius: 'var(--mantine-radius-md)',
          border: '1px solid var(--modal-border)',
          backdropFilter: 'blur(10px)',
          boxShadow: 'var(--mantine-shadow-sm)',
        }}
      >
        {item.chart?.type !== 'scorecard' && (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(8),
              padding: `${rem(12)} ${rem(16)}`,
              borderBottom: '1px solid var(--modal-border)',
              backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
            }}
          >
            <Box data-chart-icon style={{ display: 'flex', alignItems: 'center' }}>
              {isChart ? (
                <BarChart3 size={16} className="text-muted-foreground" />
              ) : (
                <TableIcon size={16} className="text-muted-foreground" />
              )}
            </Box>
            <Text size="sm" fw={600} className="truncate" style={{ flex: 1 }} data-chart-title>
              {item.chart?.config.title || item.report?.name || "Untitled Item"}
            </Text>
            {isChart && (
              <Tooltip label="Share image">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => handleShare(item)}
                  data-chart-action
                >
                  <Share2 size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Box>
        )}

        <Box style={{ flex: 1, overflow: 'hidden', padding: isChart ? 0 : rem(16) }}>
          {hasData ? (
            isChart ? (
              <div
                ref={setChartRef(item.id)}
                className="w-full h-full min-h-[200px]"
              />
            ) : isReport ? (
              <Box style={{ overflowX: 'auto', height: '100%' }} className="container-queries">
                <MantineTable.ScrollContainer
                  minWidth={500}
                  className={isMobile ? "max-h-[400px]" : "h-full"}
                  style={{ overflowY: 'auto' }}
                >
                  <MantineTable stickyHeader highlightOnHover>
                    <MantineTable.Thead>
                      <MantineTable.Tr>
                        {(item.data.columns && item.data.columns.length > 0
                          ? [...item.data.columns].sort((a, b) => (a.order || 0) - (b.order || 0))
                          : (item.data.rows[0] ? Object.keys(item.data.rows[0]).map((key, idx) => ({ name: key, displayName: key, dataType: 'string', order: idx })) : [])
                        ).map((col: any) => (
                          <MantineTable.Th
                            key={col.name}
                            style={{
                              whiteSpace: 'nowrap',
                              textAlign: col.dataType === 'numeric' ? 'right' : 'left',
                              backgroundColor: colorScheme === 'dark' ? 'rgba(30, 30, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(5px)',
                            }}
                          >
                            {col.displayName || col.name}
                          </MantineTable.Th>
                        ))}
                      </MantineTable.Tr>
                    </MantineTable.Thead>
                    <MantineTable.Tbody>
                      {item.data.rows.map((row, idx) => {
                        const cols = (item.data.columns && item.data.columns.length > 0
                          ? [...item.data.columns].sort((a, b) => (a.order || 0) - (b.order || 0))
                          : (item.data.rows[0] ? Object.keys(item.data.rows[0]).map((key, idx) => ({ name: key, dataType: 'string', order: idx })) : [])
                        );
                        const firstRow = item.data.rows[0];
                        const lastRow = item.data.rows[item.data.rows.length - 1];
                        const formatting = evaluateFormatting(row, cols as any, firstRow, lastRow);
                        return (
                          <MantineTable.Tr key={idx} style={formatting.row}>
                            {cols.map((col: any) => (
                              <MantineTable.Td key={col.name} style={{ whiteSpace: 'nowrap' }}>
                                <SmartCell
                                  value={row[col.name]}
                                  type={col.dataType}
                                  customStyle={formatting.cells[col.name]}
                                />
                              </MantineTable.Td>
                            ))}
                          </MantineTable.Tr>
                        );
                      })}
                    </MantineTable.Tbody>
                  </MantineTable>
                </MantineTable.ScrollContainer>
                {onItemPageChange && item.data.rowCount > (item.data.pageSize || 50) && (
                  <Box style={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid var(--modal-border)', padding: rem(8) }}>
                    <Pagination
                      total={Math.ceil(item.data.rowCount / (item.data.pageSize || 50))}
                      value={item.data.page || 1}
                      onChange={(p) => onItemPageChange(item.id, p)}
                      size="sm"
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Text size="sm" c="dimmed">Unknown item type</Text>
              </Box>
            )
          ) : (
            <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
              <Text size="sm" c="dimmed">No data available</Text>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (items.length === 0) {
    return (
      <Box
        style={{
          display: 'flex',
          height: rem(256),
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--mantine-radius-lg)',
          border: '1px dashed var(--modal-border)',
          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(5px)',
        }}
      >
        <Text c="dimmed">No items to display</Text>
      </Box>
    );
  }

  return (
    <div ref={gridContainerRef} className="dashboard-grid w-full">
      {isMobile ? (
        <div className="flex flex-col gap-4">
          {sortedItems.map((item) => (
            // Need a wrapper div for height on mobile usually
            <div
              key={item.id}
              className="w-full"
              style={{
                height: item.chart ? "350px" : "auto",
              }}
            >
              {renderItemContent(item)}
            </div>
          ))}
        </div>
      ) : (
        <GridLayout
          className="layout"
          layout={gridLayout}
          width={gridWidth}
          gridConfig={{
            cols: 24,
            rowHeight: 30,
          }}
          dragConfig={{
            enabled: false,
          }}
          resizeConfig={{
            enabled: false,
          }}
        >
          {gridItems.map((item) => (
            <div key={item.id}>{renderItemContent(item)}</div>
          ))}
        </GridLayout>
      )}
    </div>
  );
}
